import { createFileRoute, useNavigate, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { canRunDepreciation } from "@/lib/roles";
import { currentUserQueryOptions } from "@/hooks/useCurrentUser";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useCreateRun, usePreviewDepreciation } from "@/features/finance/api";
import { PERIOD_OPTIONS, currentFy, fyOptions } from "@/features/finance/fy";
import { inr } from "@/features/finance/format";
import type { DepMethod } from "@/features/finance/types";
import { useBranch } from "@/context/BranchContext";
import { useOptionValues } from "@/features/configuration/api";

const SUPPORTED_METHODS = new Set<string>(["SLM", "WDV"]);

export const Route = createFileRoute("/_authenticated/finance/depreciation/new")({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(currentUserQueryOptions());
    if (!canRunDepreciation(me.roles)) throw redirect({ to: "/finance/depreciation" });
  },
  component: NewDepRun,
});

function NewDepRun() {
  const navigate = useNavigate();
  const { currentBranchId } = useBranch();
  const [fy, setFy] = useState(currentFy());
  const [period, setPeriod] = useState<string>("Annual");
  const [method, setMethod] = useState<DepMethod>("SLM");
  const [runName, setRunName] = useState(`Depreciation FY ${currentFy()} (SLM)`);
  const [showPreview, setShowPreview] = useState(false);
  const create = useCreateRun();
  const preview = usePreviewDepreciation(showPreview ? { fy, period, branchId: currentBranchId } : null);
  const { values: methodOpts } = useOptionValues("finance", "depreciation_method");

  const totalDep = (preview.data ?? []).reduce((s, r) => s + Number(r.dep_amount ?? 0), 0);

  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/finance/depreciation" })}><ArrowLeft className="mr-1 h-4 w-4" />Back</Button>
      <Card>
        <CardHeader><CardTitle>New Depreciation Run</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div className="md:col-span-2">
              <Label>Run Name</Label>
              <Input value={runName} onChange={(e) => setRunName(e.target.value)} />
            </div>
            <div>
              <Label>FY</Label>
              <Select value={fy} onValueChange={(v) => { setFy(v); setRunName(`Depreciation FY ${v} (${method})`); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{fyOptions().map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Period</Label>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PERIOD_OPTIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Method</Label>
              <Select value={method} onValueChange={(v) => { if (!SUPPORTED_METHODS.has(v)) return; setMethod(v as DepMethod); setRunName(`Depreciation FY ${fy} (${v})`); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(methodOpts.length ? methodOpts.map((o) => ({ value: o.value, label: o.label })) : [{ value: "SLM", label: "SLM — Straight Line" }, { value: "WDV", label: "WDV — Written Down Value" }]).map((o) => {
                    const supported = SUPPORTED_METHODS.has(o.value);
                    return (
                      <SelectItem key={o.value} value={o.value} disabled={!supported}>
                        {o.label}{!supported ? " (not supported by calculation engine)" : ""}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowPreview(true)}>Preview</Button>
            <Button
              disabled={create.isPending}
              onClick={async () => {
                try {
                  const run = await create.mutateAsync({ run_name: runName, fy, period, method });
                  toast.success("Draft run created");
                  navigate({ to: "/finance/depreciation/$runId", params: { runId: run.id } });
                } catch (e) { toast.error((e as Error).message); }
              }}
            >Save as Draft</Button>
          </div>
        </CardContent>
      </Card>

      {showPreview && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Preview — {preview.data?.length ?? 0} assets · Total dep {inr(totalDep)}</CardTitle>
          </CardHeader>
          <CardContent>
            {preview.isLoading ? <p className="text-sm text-muted-foreground">Calculating…</p> : (
              <div className="max-h-[60vh] overflow-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Category</TableHead>
                    <TableHead className="text-right">Opening NBV</TableHead>
                    <TableHead className="text-right">Days</TableHead>
                    <TableHead className="text-right">Dep</TableHead>
                    <TableHead className="text-right">Closing NBV</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {(preview.data ?? []).map((r) => (
                      <TableRow key={r.asset_id}>
                        <TableCell className="font-mono text-xs">{r.asset_code}</TableCell>
                        <TableCell>{r.asset_name}</TableCell>
                        <TableCell>{r.category_name ?? "—"}</TableCell>
                        <TableCell className="text-right">{inr(r.opening_nbv)}</TableCell>
                        <TableCell className="text-right">{r.days_used}</TableCell>
                        <TableCell className="text-right font-medium">{inr(r.dep_amount)}</TableCell>
                        <TableCell className="text-right">{inr(r.closing_nbv)}</TableCell>
                      </TableRow>
                    ))}
                    {!preview.isLoading && (preview.data ?? []).length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">No eligible assets for this period.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
